<template>
  <div>
    <entity-card />

    <template v-if="useDynamicForm">
      <md-card ref="typeCard">
        <md-card-header data-background-color="green">
          <h4 class="title">
            1 - Selecione o Tipo de Chamado
          </h4>
        </md-card-header>
        <md-card-content class="options">
          <new-ticket-choice
            label="Requisição"
            tooltip="Solicitações que não sejam originadas a partir de um incidente ou que não coloque em risco o funcionamento dos equipamentos"
            icon="https://i.ibb.co/VvL2WB9/426878-200.png"
            :highlight="ticketType === null || ticketType === 2"
            @click="selectType(2)"
          />

          <new-ticket-choice
            label="Incidente"
            tooltip="Perda parcial ou total de um equipamento ou sistema executar a função ao qual ele foi projetado (perda parcial: falha / perda total: quebra)"
            icon="https://i.ibb.co/vkv5tSN/warning.png"
            :highlight="ticketType === null || ticketType === 1"
            @click="selectType(1)"
          />
        </md-card-content>
      </md-card>

      <md-card
        v-if="ticketType !== null"
        ref="categoryCard"
      >
        <md-card-header data-background-color="green">
          <h4 class="title">
            2 - Selecione a Categoria
          </h4>
        </md-card-header>
        <md-card-content class="options">
          <new-ticket-choice
            v-for="category in categoriesList"
            :key="category.id"
            :label="category.name"
            :icon="category.comment"
            :highlight="selectedCategory === null || selectedCategory.id === category.id"
            @click="selectCategory(category)"
          />
        </md-card-content>
      </md-card>

      <md-card
        v-if="selectedCategory !== null && equipmentTypeList.length > 0"
        ref="equipmentTypeCard"
      >
        <md-card-header data-background-color="green">
          <h4 class="title">
            3 - Selecione o Tipo de Equipamento ou Detalhamento
          </h4>
        </md-card-header>
        <md-card-content class="options">
          <new-ticket-choice
            v-for="equipmentType in equipmentTypeList"
            :key="equipmentType.id"
            :label="equipmentType.name"
            :icon="equipmentType.comment"
            :highlight="selectedEquipmentType === null || selectedEquipmentType.id === equipmentType.id"
            @click="selectEquipmentType(equipmentType)"
          />
        </md-card-content>
      </md-card>

      <md-card
        v-if="selectedEquipmentType !== null && equipmentList.length > 0"
        ref="equipmentCard"
      >
        <md-card-header data-background-color="green">
          <h4 class="title">
            4 - Selecione o Equipamento
          </h4>
        </md-card-header>
        <md-card-content class="options">
          <new-ticket-choice
            v-for="equipment in equipmentList"
            :key="equipment.id"
            :label="equipment.name"
            :icon="equipment.comment"
            :highlight="selectedEquipment === null || selectedEquipment.id === equipment.id"
            @click="selectEquipment(equipment)"
          />
        </md-card-content>
      </md-card>

      <md-card
        v-if="selectedEquipment !== null && equipmentList.length > 0"
        ref="eventCard"
      >
        <md-card-header data-background-color="green">
          <h4 class="title">
            5 - Selecione o Evento
          </h4>
        </md-card-header>
        <md-card-content class="options">
          <new-ticket-choice
            v-for="event in eventList"
            :key="event.id"
            :label="event.name"
            :icon="event.comment"
            :highlight="selectedEvent === null || selectedEvent.id === event.id"
            @click="selectEvent(event)"
          />
        </md-card-content>
      </md-card>

      <md-card
        v-if="selectedCategory !== null"
        ref="descriptionCard"
      >
        <md-card-header data-background-color="green">
          <h4 class="title">
            DETALHE
          </h4>
          <p class="category">
            Preencha os campos
          </p>
        </md-card-header>

        <md-card-content>
          <div class="md-layout">
            <div class="md-layout-item md-small-size-100 md-size-100">
              <md-field>
                <label>DESCRICAO</label>
                <md-textarea v-model="ticketDescription" />
              </md-field>
            </div>

            <div class="md-layout-item md-size-100 text-right">
              <md-button
                class="md-raised md-success"
                @click="confirmForm()"
              >
                ADICIONAR
              </md-button>
            </div>
          </div>
        </md-card-content>
      </md-card>

      <fab-button
        :hidden="!shouldShowScrollToTop"
        @click="scrollToTop()"
      >
        arrow_upward
      </fab-button>
    </template>

    <new-ticket-form
      v-else
      data-background-color="green"
      :all-categories="allCategories"
    />
  </div>
</template>

<script>
import debounce from "lodash.debounce";

import EntityCard from "./GlpiForms/EntityCard";
import NewTicketForm from "./GlpiForms/NewTicketForm";
import NewTicketChoice from "./GlpiForms/NewTicketChoice";

import FabButton from "../components/FabButton";

import { originalEquipmentModels, userService } from "../_services";
import { getUserSession } from "../_services/localstorage";
import { DateTime } from "luxon";
import { EventBus, events } from "../_helpers/event-bus";
import { getEntityById } from "@/_services/entity.service";

const TICKET_TYPE_CONVERSION = [ "-", "Incidente", "Requisição" ];
const SIMPLE_FORMS_ENTITIES = new Set([ 1, 2, 119, 708, 709, 1021, 1033, 1034, 1035 ]);

export default {
  components: {
    FabButton,
    NewTicketChoice,
    NewTicketForm,
    EntityCard,
  },
  data() {
    return {
      user: {},
      entity: null,

      categoriesList: [],
      selectedCategory: null,

      equipmentTypeList: [],
      selectedEquipmentType: null,

      equipmentList: [],
      selectedEquipment: null,

      eventList: [],
      selectedEvent: null,

      ticketType: null,
      ticketDescription: null,

      allCategories: [],

      shouldShowScrollToTop: false,
    };
  },
  computed: {
    useDynamicForm() {
      const { activeentity } = this.user;
      return !SIMPLE_FORMS_ENTITIES.has(activeentity);
    },
  },
  async created() {
    this.$progress.start();

    await this.onEntityChange();

    this.$progress.stop();

    EventBus.$on(events.CHANGED_ENTITY, this.onEntityChange);
    window.addEventListener("scroll", this.onScrollEvent);
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.onScrollEvent);
    EventBus.$off(events.CHANGED_ENTITY, this.onEntityChange);
  },
  methods: {
    onScrollEvent: debounce(function () {
      this.onScroll(this);
    }, 30),
    onScroll(ctx) {
      const pageOffset = window.scrollY;
      const windowHeight = window.innerHeight;

      ctx.shouldShowScrollToTop = pageOffset > windowHeight / 2;
    },
    async onEntityChange() {
      this.user = getUserSession();

      this.resetType();
      this.generateRenameAndRemoveList();

      await Promise.allSettled([
        this.retrieveCategories(),
        this.retrieveEntity(),
      ]);
    },
    async retrieveEntity() {
      this.entity = await getEntityById(this.user.activeentity);
    },
    generateRenameAndRemoveList() {
      this.renameCategoryMap = new Map();
      this.removeCategorySet = new Set();

      const {equipmentModels} = this.user;

      originalEquipmentModels.forEach(originalEquipmentModel => {
        const userEquipmentModel = equipmentModels.find((equipment) => equipment.itemtype === originalEquipmentModel.itemtype);
        if (!userEquipmentModel) {
          this.removeCategorySet.add(originalEquipmentModel.name);
          return;
        }

        if (originalEquipmentModel.name !== userEquipmentModel.name) {
          this.renameCategoryMap.set(
            originalEquipmentModel.name,
            userEquipmentModel.name,
          );
        }
      });
    },
    async retrieveCategories() {
      const {list: categoriesList} = await userService.searchItilCategories();

      const wasCategoryNotDeleted = category => !this.removeCategorySet.has(category.name);
      const renameCategory = category => ({
        ...category,
        name: this.renameCategoryMap.get(category.name) || category.name,
      });

      this.allCategories = categoriesList
        .filter(wasCategoryNotDeleted)
        .map(renameCategory);
    },
    async confirmForm() {
      const modality = this.ticketType === 2 ? 4 : 5;
      const status = 1;
      const priority = 2;

      const now = DateTime.now();
      const title = [
        this.entity.name.toUpperCase(),
        TICKET_TYPE_CONVERSION[this.ticketType].toUpperCase(),
        this.ticketCategory.completename.replace(/>/g, "-" ).toUpperCase(),
        now.toFormat("dd/MM/yyyy"),
      ].join(" - ");

      if (!this.ticketDescription) {
        this.ticketDescription = title;
      }

      const { data: createdTicket } = await userService.addTicket(
        this.user.token,
        title,
        this.ticketDescription,
        modality,
        priority,
        status,
        this.ticketType,
        this.ticketCategory.id
      );

      if (this.selectedEquipment) {
        await userService.relateAssetToTicket(
          this.user.token,
          this.ticketEquipmentType,
          this.selectedEquipment.id,
          createdTicket.id,
        );
      }

      this.$router.replace("/activities");
    },
    resetType() {
      this.ticketType = null;
      this.resetCategory();
    },
    resetCategory() {
      this.categoriesList = [];
      this.selectedCategory = null;
      this.resetEquipmentType();
    },
    resetEquipmentType() {
      this.equipmentTypeList = [];
      this.selectedEquipmentType = null;
      this.ticketEquipmentType = null;
      this.resetEquipment();
    },
    resetEquipment() {
      this.equipmentList = [];
      this.selectedEquipment = null;
      this.resetEvent();
    },
    resetEvent() {
      this.eventList = [];
      this.selectedEvent = null;
    },
    async scrollIntoCard(cardType) {
      await this.$nextTick();

      const cardElement = this.$refs[`${cardType}Card`].$el;

      cardElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        left: 0, 
        behavior: "smooth"
      });
    },
    selectType(type) {
      this.ticketType = type;

      this.resetCategory();

      const categoryDoesNotHaveParent = category => !category.parent;

      this.categoriesList = this.allCategories.filter(category =>
        categoryDoesNotHaveParent(category) &&
        this.isCategoryOfSelectedTicketType(category)
      );

      // If stricted remove all other options
      if (this.user.categoriesStyle === "stricted") {
        const isCategoryOnUserEquipmentModels = category => this.user.equipmentModels
          .find(equipmentModel => category.name === equipmentModel.name);

        this.categoriesList = this.categoriesList
          .filter(isCategoryOnUserEquipmentModels);
      }

      this.scrollIntoCard("category");
    },
    async selectCategory(selectedCategory) {
      this.$progress.start();

      this.ticketCategory = selectedCategory;
      this.selectedCategory = selectedCategory;

      this.resetEquipmentType();

      const equipmentModels = [...this.user.equipmentModels];
      equipmentModels.reverse();

      const userEquipment = this.user.equipmentModels
        .filter(equipmentModel => equipmentModel.name === selectedCategory.name)
        .at(-1);

      if (!userEquipment) {
        this.$progress.stop();
        await this.scrollIntoCard("description");
        return;
      }

      const equipmentType = userEquipment && userEquipment.itemtype || null;

      this.ticketEquipmentType = equipmentType;

      const { list: equipmentTypes } = await userService.searchEquipmentTypes(this.user.token, equipmentType);

      const { list: equipments } = await userService.searchEquipmentsByTypeSimple(this.user.token, equipmentType);
      this.allEquipments = equipments;

      const equipmentTypesToRemove = new Set();
      for (const equipmentType of equipmentTypes) {
        const equipmentWithSameType = equipments.find(equipment => equipment.type === equipmentType.name);
        if (!equipmentWithSameType) {
          equipmentTypesToRemove.add(equipmentType.name);
        }
      }

      const wasCategoryNotRemoved = category => !equipmentTypesToRemove.has(category.name);
      const filterUnique = (category, index, array) => array.findIndex(category2 => category.name === category2.name) === index;

      this.equipmentTypeList = this.allCategories
        .filter(equipmentType =>
          this.isCategoryOfSelectedTicketType(equipmentType) &&
          this.isCategoryChildOf(selectedCategory, equipmentType) &&
          wasCategoryNotRemoved(equipmentType)
        )
        .filter(filterUnique);

      this.$progress.stop();

      await this.scrollIntoCard("equipmentType");
    },
    selectEquipmentType: function (selectedEquipmentType) {
      this.selectedEquipmentType = selectedEquipmentType;

      this.resetEquipment();

      const isEquipmentOfType = equipment => equipment.type === selectedEquipmentType.name;

      this.equipmentList = this.allEquipments
        .filter(isEquipmentOfType)
        .map(equipment => ({
          ...equipment,
          comment: equipment.comment || selectedEquipmentType.comment,
        }));

      this.ticketCategory = selectedEquipmentType;

      this.scrollIntoCard(this.equipmentList.length ? "equipment" : "description");
    },
    selectEquipment(selectedEquipment) {
      this.selectedEquipment = selectedEquipment;

      this.resetEvent();

      this.eventList = this.allCategories.filter(event =>
        this.isCategoryOfSelectedTicketType(event) &&
        this.isCategoryChildOf(this.selectedEquipmentType, event)
      );

      this.scrollIntoCard(this.eventList.length ? "event" : "description");
    },
    selectEvent(event) {
      this.selectedEvent = event;
      this.ticketCategory = event;
      this.scrollIntoCard("description");
    },

    // Helper functions
    isCategoryOfSelectedTicketType(category) {
      return this.ticketType === 2 && category.request === 1 ||
        this.ticketType === 1 && category.incident === 1;
    },
    isCategoryChildOf(parent, category) {
      return category.parent === parent.completename;
    }
  }
};
</script>

<style scoped lang="scss">
.options {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 20px;
}
</style>
